<script lang="ts" setup>
import { defineComponent, PropType } from 'vue'

import { LeaderboardTable as LeaderboardTableType } from '@base/_types/LeaderboardTable'

import USTable from '@base/components/USTable/USTable.vue'
import USTableRow from '@base/components/USTableRow/USTableRow.vue'
import USAvatar from '@base/components/USAvatar/USAvatar.vue'

defineComponent({
  name: 'LeaderboardTable'
})

defineProps({
  users: {
    type: Array as PropType<LeaderboardTableType[]>,
    required: true
  }
})

const columns = ['Rank', 'Player', 'Points']
</script>

<template>
  <USTable data-test="leaderboard-table" :columns="columns">
    <template #default="slotProps">
      <USTableRow
        v-for="user in users"
        :key="user.id"
        v-bind="slotProps"
        :class="[user.isUser ? 'leaderboard-table__user' : '']"
      >
        <template #0>
          <span class="display--d2--semibold">{{ user.rank }}</span>
        </template>
        <template #1>
          <div class="leaderboard-table__user-info">
            <USAvatar :src="user.avatar" :initials="user.username[0]" />
            <span class="leaderboard-table__username display--d2--semibold">
              {{ user.username }}
            </span>
          </div>
        </template>
        <template #2>
          <span class="body--p1--medium">{{ user.points }} pts</span>
        </template>
      </USTableRow>
    </template>
  </USTable>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

.leaderboard-table {
  color: var(--color--neutral-a1);

  &__user {
    align-items: center;
    background: var(--color--secondary-c2);
    position: sticky;
    bottom: 0;
  }

  &__user-info {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
  }

  &__username {
    margin-left: var(--spacing--6);
  }
}

:deep(thead tr) {
  border: none;
  border-bottom: solid var(--line-weight--light) var(--color--neutral-a4);
  border-radius: 0;
  clip: unset;
  clip-path: none;
  height: auto;
  position: relative;
  width: 100%;
}

:deep(tbody tr) {
  border: none;
  border-radius: 0;
  margin-bottom: 0px;
  padding: 0;
  transition: none;
  will-change: unset;
}

:deep(tbody td) {
  align-items: center;
}

:deep(tr) {
  display: flex;
}

:deep(tr td) {
  &::before {
    content: none;
    flex-basis: 0;
  }

  &:first-child {
    align-self: center;
    flex-basis: 18%;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-basis: 20%;
    }
  }

  &:nth-child(2) {
    flex-basis: 50%;
  }

  &:nth-child(3) {
    align-self: center;
    flex-basis: 32%;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-basis: 30%;
    }
  }
}

:deep(th) {
  color: var(--color--neutral-a3);
  display: inline-block;

  &:first-child {
    flex-basis: 18%;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-basis: 20%;
    }
  }

  &:nth-child(2) {
    flex-basis: 50%;
  }

  &:nth-child(3) {
    flex-basis: 32%;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-basis: 30%;
    }
  }
}
</style>
